import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Languages, Plus, Trash2, Loader2, Mars, Venus } from "lucide-react";

export function NamePackDialog({ open, onOpenChange, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [names, setNames] = useState([]);
  const [name, setName] = useState("");
  const [gender, setGender] = useState("female");
  const [culture, setCulture] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.listNamePacks()
      .then((r) => setNames(r.names || []))
      .catch(() => toast.error("Could not load names"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const add = async () => {
    const n = name.trim();
    if (!n) { toast.error("Enter a first name"); return; }
    setSaving(true);
    try {
      await api.createNamePack(n, gender, culture.trim() || null);
      setName(""); setCulture("");
      toast.success(`"${n}" will now be guessed as ${gender}`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save name");
    } finally { setSaving(false); }
  };

  const remove = async (p) => {
    try { await api.deleteNamePack(p.id); setNames((ns) => ns.filter((x) => x.id !== p.id)); onChanged?.(); }
    catch { toast.error("Could not delete"); }
  };

  const grouped = useMemo(() => {
    const g = {};
    names.forEach((n) => { const key = n.culture || "Other"; (g[key] = g[key] || []).push(n); });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [names]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto" data-testid="name-packs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-[#082D4A]">
            <Languages size={22} /> Name gender packs
          </DialogTitle>
          <DialogDescription>
            Teach the tree first names from your family's culture so it can pre-guess Male/Female when
            you add a new relative. Your additions override the built-in guesses.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-xl border border-[#D8E0E8] bg-[#F4F6F8] p-3">
            <div className="grid grid-cols-1 gap-2">
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="First name (e.g. Kavya)" data-testid="name-pack-name-input"
                onKeyDown={(e) => { if (e.key === "Enter") add(); }} />
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setGender("female")} data-testid="name-pack-gender-female"
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${gender === "female" ? "border-[#DB2777] bg-[#DB2777] text-white" : "border-[#D8E0E8] bg-white text-[#082D4A] hover:bg-[#E8EEF3]"}`}>
                  <Venus size={15} /> Female
                </button>
                <button type="button" onClick={() => setGender("male")} data-testid="name-pack-gender-male"
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${gender === "male" ? "border-[#2563EB] bg-[#2563EB] text-white" : "border-[#D8E0E8] bg-white text-[#082D4A] hover:bg-[#E8EEF3]"}`}>
                  <Mars size={15} /> Male
                </button>
              </div>
              <Input value={culture} onChange={(e) => setCulture(e.target.value)} placeholder="Culture / origin (optional, e.g. Indian)" data-testid="name-pack-culture-input" />
              <Button onClick={add} disabled={saving} data-testid="name-pack-add-button" className="w-full bg-[#082D4A] hover:bg-[#08223A]">
                {saving ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Saving…</> : <><Plus size={15} className="mr-1.5" /> Add name</>}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-8 text-[#64748B]"><Loader2 className="animate-spin" size={16} /> Loading…</div>
          ) : names.length === 0 ? (
            <p className="py-6 text-center text-sm text-[#64748B]" data-testid="name-pack-empty">No custom names yet. Add the ones your family uses above.</p>
          ) : (
            <div className="max-h-72 space-y-3 overflow-y-auto">
              {grouped.map(([cult, list]) => (
                <div key={cult}>
                  <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{cult}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {list.map((p) => (
                      <span key={p.id} data-testid={`name-pack-row-${p.name}`}
                        className={`inline-flex items-center gap-1 rounded-full border py-0.5 pl-2.5 pr-1 text-xs font-medium ${p.gender === "female" ? "border-[#DB2777]/40 bg-[#DB2777]/10 text-[#9D174D]" : "border-[#2563EB]/40 bg-[#2563EB]/10 text-[#1E40AF]"}`}>
                        {p.gender === "female" ? <Venus size={11} /> : <Mars size={11} />}
                        <span className="capitalize">{p.name}</span>
                        <button onClick={() => remove(p)} data-testid={`name-pack-delete-${p.name}`} className="ml-0.5 rounded-full p-0.5 hover:bg-black/10"><Trash2 size={12} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
